import { Box, Button, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import axios from "../../../../src/services/axios";
import { UserCtx } from "../../../interfaces";
import { UserContext } from "../../../context/UserContext";
import { PopUpDialog } from "../../v2/utils/PopUpDialog";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { ErrorToast } from "../../v2/utils/Toast";
import { IconButtonContained } from "../../v2/utils/IconButtonContained";

const useStyles = makeStyles((theme) => ({
  banner: {
    backgroundColor: "#80449F",
    position: "fixed",
    bottom: "16px",
    left: "50%",
    transform: "translateX(-50%)",
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    borderRadius: "8px",
    overflow: "hidden",
    zIndex: 500,
    transition: "all 300ms ease-out",
    // Respect reduced-motion users → remove animations
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  },

  collapsed: {
    left: "16px !important",
    transform: "translateX(0) !important",
    width: "92px !important",
    justifyContent: "flex-start",
    paddingRight: "0px !important",
  },

  button: {
    background: "#4E2960",
    padding: "8px",
    borderRadius: "4px",
    height: 35,
    fontFamily: "Roboto",
    fontWeight: 600,
    fontStyle: "normal",

    "&:hover": {
      background: "#341B40",
    },
    "&:focus-visible": {
      border: "2px solid #FFFFFF",
    },
  },
}));

interface BottomBannerProps {
  sticky?: boolean;
  height?: number;
  width?: number;
  onClose?: () => void;
}

export const BottomBanner = (props: BottomBannerProps) => {
  const classes = useStyles();
  const { height, width } = props;

  const [showResetModal, setShowResetModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const userCtx = useContext(UserContext) as UserCtx;

  const handleToggleLockUnlockModules = () => {
    userCtx.setAssetLockOverride(!userCtx.assetLockOverride);
  };

  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    toolbarRef.current?.focus();
  }, []);

  return (
    <>
      <Box
        ref={toolbarRef}
        tabIndex={-1}
        role="toolbar"
        aria-label="Student view"
        className={`${classes.banner} ${collapsed ? classes.collapsed : ""}`}
        sx={{
          width: collapsed ? "92px" : width ? `${width}px` : "auto",
          height: height ? `${height}px` : "48px",
          px: "16px",
        }}>
        {/* Chevron Icon — Toggle Collapse */}
        <IconButtonContained
          tooltipLabel={collapsed ? "Expand" : "Collapse"}
          tooltipPlacement="top"
          color="info"
          sx={{
            width: 24,
            height: 24,
            backgroundColor: "rgba(255, 255, 255, 0.30) !important",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "background-color 150ms ease, transform 300ms ease",
            transform: collapsed ? "rotate(180deg)" : "none",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.40) !important",
            },
            "&:focus-visible": {
              border: "1px solid #191919",
            },
          }}
          onClick={() => setCollapsed((prev) => !prev)}>
          <ChevronLeftIcon sx={{ color: "#FFF", fontSize: 18 }} />
        </IconButtonContained>
        {/* Student View label — Visible always */}
        <Box
          tabIndex={-1}
          sx={{
            display: "flex",
            alignItems: "center",
            mx: "16px",
          }}>
          <i className="fa-regular fa-graduation-cap" style={{ color: "#FFFFFF" }} />
          {!collapsed && (
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "100%",
                color: "#FFFFFF",
                ml: "4px",
              }}
              component="div">
              <span>Student view</span>
            </Typography>
          )}
        </Box>

        {/* Right side elements — hidden when collapsed */}
        {!collapsed && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                fontWeight: 600,
                mx: "16px",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={handleToggleLockUnlockModules}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "100%",
                  color: "#FFFFFF",
                }}
                component="div">
                <span>Unlock all modules</span>
              </Typography>

              <Switch
                size="small"
                color="default"
                checked={!!userCtx.assetLockOverride}
                onClick={(e) => e.stopPropagation()}
                onChange={handleToggleLockUnlockModules}
                inputProps={{
                  "aria-label": "Unlock all modules",
                }}
                sx={{
                  width: 32,
                  height: 19,
                  padding: 0,
                  marginLeft: 1,
                  border: "2px solid white",
                  borderRadius: "12px",

                  "&:has(.MuiSwitch-switchBase.Mui-focusVisible)": {
                    outline: "2px solid #000",
                    outlineOffset: "2px",
                  },

                  "& .MuiSwitch-switchBase": {
                    transitionDuration: "200ms",
                    padding: 0,
                    margin: "2px",

                    "&.Mui-checked": {
                      transform: "translateX(12px)",
                      backgroundColor: "transparent",
                    },
                  },

                  "& .MuiSwitch-thumb": {
                    width: 11,
                    height: 11,
                  },

                  "& .MuiSwitch-track": {
                    backgroundColor: "transparent",
                    opacity: 1,
                    borderRadius: "12px",
                  },
                }}
              />
            </Box>

            <Tooltip
              placement="bottom"
              arrow
              title="Leave student view"
              aria-hidden={true}>
              <Button
                tabIndex={0}
                aria-label="Exit student view"
                className={classes.button}
                sx={{ width: "43px !important" }}
                onClick={props.onClose}>
                Exit
              </Button>
            </Tooltip>

            <Tooltip
              placement="bottom"
              arrow
              title="Start over and clear course progress"
              aria-hidden={true}>
              <Button
                tabIndex={0}
                aria-label="Reset course progress"
                className={classes.button}
                sx={{ width: "57px !important", ml: "8px" }}
                onClick={() => setShowResetModal(true)}>
                Reset
              </Button>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Popup Dialog */}
      <PopUpDialog
        ctaFooterVariant={"default"}
        promptOpen={showResetModal}
        handleClose={() => setShowResetModal(false)}
        handleOkClick={async () => {
          try {
            if (!userCtx.studentCurrentClass?.id) {
              throw new Error("User is not associated with any class.");
            }

            const response = await axios.delete(`/reset-student-view-progress`, {
              params: {},
            });

            if (response.status === 200) {
              window.location.reload();
            } else throw new Error();

            setShowResetModal(false);
          } catch (e: any) {
            console.error("Failed to reset progress", e);
            ErrorToast("Error in resetting the progress");
            throw e;
          }
        }}
        handleCancelClick={() => setShowResetModal(false)}
        popupTitle={`Reset Student View progress?`}
        cancelButtonLabel={"Cancel"}
        okButtonLabel={"Reset"}>
        <Stack direction={"column"} spacing={4}>
          <Typography id="reset-student-view-description" variant={"body1"}>
            This will clear all the progress you’ve made in Student View, including quiz
            scores, course progress, and unlocked modules or lessons. Resetting Student
            View does not impact reporting data. You can always explore the course again
            from the beginning.
          </Typography>
        </Stack>
      </PopUpDialog>
    </>
  );
};
